import {
  AsyncThunk,
  AsyncThunkConfig,
  AsyncThunkOptions,
  AsyncThunkPayloadCreator,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import {
  reportError,
  reportWarning,
  formatError,
} from "../helpers/error-helper";
import * as notificationActions from "./notifications";
import { AppDispatch, AppThunkAction, RootState } from "../redux-store";
import { isAxiosError } from "axios";
import { isDatabaseUnavailableResponse } from "../axios";
import * as Sentry from "@sentry/react";

// The thunk config createAppAsyncThunk is made with.
type AppThunkConfig = { state: RootState; dispatch: AppDispatch };
// A thunk's own config on top of the app's, the way createAsyncThunk merges
// them (its own helper for this isn't exported).
type WithAppThunkConfig<C> = C & Omit<AppThunkConfig, keyof C>;

// What createErrorReportingAsyncThunk takes on top of createAsyncThunk's
// options.
type ErrorReportingOptions = {
  // A request the user didn't ask for (a background refresh): a failure is
  // still reported, but not shown to the user.
  skipNotification?: boolean;
};

// The two forms createAppAsyncThunk has (the app's thunk config, or a
// thunk's own config on top of it), taking the error reporting's options too.
type CreateErrorReportingAsyncThunk = {
  <Returned, ThunkArg = void>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, AppThunkConfig>,
    options?: AsyncThunkOptions<ThunkArg, AppThunkConfig> &
      ErrorReportingOptions
  ): AsyncThunk<Returned, ThunkArg, AppThunkConfig>;
  <Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<
      Returned,
      ThunkArg,
      WithAppThunkConfig<ThunkApiConfig>
    >,
    options?: AsyncThunkOptions<
      ThunkArg,
      WithAppThunkConfig<ThunkApiConfig>
    > &
      ErrorReportingOptions
  ): AsyncThunk<Returned, ThunkArg, WithAppThunkConfig<ThunkApiConfig>>;
};

export const defaultErrorAction = () =>
  notificationActions.notifyL({
    type: "error",
    namespace: "notifications",
    key: "unexpectedError",
  });

const isNetworkError = (e: unknown) => {
  if (
    isAxiosError(e) ||
    (e &&
      typeof e === "object" &&
      "message" in e &&
      typeof e.message === "string")
  )
    return e.message === "Network Error" || e.message === "Request aborted";
  else return false;
};

// Axios's own timeout timer firing. Investigated via Sentry DISPATCHER-8F /
// DORA-333: every sampled occurrence never reached our backend at all (no
// server-side request log), so in practice this behaves like isNetworkError
// above rather than a slow/hanging backend. Kept as a separate check rather
// than folded into isNetworkError because a genuinely slow backend would
// throw the identical client-side message and we have no way to tell the two
// apart from here.
const isTimeoutError = (e: unknown) =>
  isAxiosError(e) && e.message === "timeout exceeded";

// The org's own finance integration (e.g. e-conomic) credential is invalid —
// expected, customer-side state that requires them to reconnect, not a bug
// on our end, so it shouldn't be reported like an unexpected failure.
export const isFinanceIntegrationTokenInvalidError = (e: unknown) =>
  isAxiosError(e) &&
  (e.response?.data as { code?: string } | undefined)?.code ===
    "FINANCE_INTEGRATION_TOKEN_INVALID";

// The finance system turned an invoice down for reasons the server recognised
// (barred product/customer, deleted delivery location). The reasons are on the
// failed invoice for the user to act on — expected, not a bug to report.
export const isFinanceInvoiceRejectedError = (e: unknown) =>
  isAxiosError(e) &&
  (e.response?.data as { code?: string } | undefined)?.code ===
    "FINANCE_INVOICE_REJECTED";

// The server couldn't reach the org's finance system at all (connection timed
// out / unreachable) even after retrying — a network blip between us and the
// finance provider, not a bug, so tell the user rather than report it.
export const isFinanceIntegrationUnreachableError = (e: unknown) =>
  isAxiosError(e) &&
  (e.response?.data as { code?: string } | undefined)?.code ===
    "FINANCE_INTEGRATION_UNREACHABLE";

const isUnauthenticatedError = (e: unknown) =>
  isAxiosError(e) && e.response?.status === 401;

// A 429 is throttling, not a bug. Investigated via Sentry DISPATCHER-D2 /
// DORA-456: the one occurrence never reached our backend, so it came from a
// proxy between the browser and us. Treated like a network error.
const isRateLimitedError = (e: unknown) =>
  isAxiosError(e) && e.response?.status === 429;

// A refusal the server meant for the user rather than for the error log. Two
// shapes reach the client:
//
//   * `{ kind: "code" }` — the body carried a `code` from dora-shared's
//     api-errors (409 { code, message }, or 400 { message, code, params } from
//     equipment and rentals). The code is stable and is what the UI must
//     translate, with `params` as the values that translation interpolates;
//     `message` is only the server's own English wording, kept for logging and
//     for API integrators who have no translations of their own.
//   * `{ kind: "message" }` — the body carried only a message (a 400 from an
//     endpoint whose refusals are not coded yet). That message is an English
//     sentence written server-side, so it is NOT safe to show verbatim: a
//     Danish user would read English. Until those endpoints get codes of their
//     own, a caller should show its own translated fallback and treat this
//     text as diagnostic only.
//
// The union is deliberate: a caller has to look at `kind` and so has to decide
// whether it has something translatable, instead of blindly rendering text.

// The values the refusal's sentence talks about — an equipment number, a
// status, a rental number, a count — for the UI's own translation of it.
export type ServerRejectionParams = Record<string, string | number>;

export type ServerRejection =
  | {
      kind: "code";
      code: string;
      params: ServerRejectionParams;
      message: string | null;
    }
  | { kind: "message"; message: string };

// What to show the user for a rejection the server aimed at them. A coded
// rejection is the translatable one: the code is stable while the text can be
// reworded per language, and `params` are what that text interpolates. A code
// this build has no translation for falls back to the caller's own generic
// message — never to the server's `message`, which is English prose whatever
// language the user runs the app in. Only an uncoded refusal has nothing else
// to show, and there that English sentence is all there is.
export const serverRejectionText = (
  rejection: ServerRejection | null | undefined,
  translateCode: (code: string, params: ServerRejectionParams) => string | null,
  fallback: string
): string => {
  if (!rejection) return fallback;
  if (rejection.kind === "code") {
    return translateCode(rejection.code, rejection.params) ?? fallback;
  }
  return rejection.message;
};

// Only the values a sentence can interpolate are carried over; anything else
// in the body is ignored rather than passed to the translation as-is.
const rejectionParams = (params: unknown): ServerRejectionParams => {
  if (!params || typeof params !== "object") return {};
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(
      ([, value]) => typeof value === "string" || typeof value === "number"
    )
  ) as ServerRejectionParams;
};

export const serverRejectionMessage = (
  err: unknown
): ServerRejection | null => {
  if (!isAxiosError(err)) return null;
  const status = err.response?.status;
  // 400: request validation refused it. 409: a named conflict (duplicate
  // number/name, type still in use). Anything else is not a refusal aimed at
  // the user and should go through the normal reporting/toast path.
  if (status !== 400 && status !== 409) return null;
  const data = err.response?.data as
    | { code?: unknown; message?: unknown; params?: unknown }
    | undefined;
  const message =
    typeof data?.message === "string" && data.message.trim()
      ? data.message
      : null;
  if (typeof data?.code === "string" && data.code)
    return {
      kind: "code",
      code: data.code,
      params: rejectionParams(data.params),
      message,
    };
  return message === null ? null : { kind: "message", message };
};

// Retries `load` until it succeeds or `attempts` runs out, re-throwing the last
// error. Use it inside a thunk's action so createErrorReportingAsyncThunk
// reports only the final failure, not every attempt.
export async function withRetries<T>(
  load: () => Promise<T>,
  { attempts = 3, delayMs = 1000 } = {}
): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await load();
    } catch (e) {
      if (attempt >= attempts) throw e;
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Why the server refused a 4xx request, as a short stable string for Sentry
// grouping. Sentry groups by stack trace alone, so every 4xx thrown from one
// axios call site lands in a single issue whatever the cause (DISPATCHER-8M
// mixed a deleted-template reference with many-to-many stop payloads). The
// server's bodies come in several shapes, most stable first:
//
//   * `{ code }` — a coded refusal from dora-shared's api-errors
//   * `{ error, validationErrors: [{ code }] }` — cargo payload validation;
//     the sub-codes say which rule failed
//   * `{ error }` / `{ message }` — a plain refusal; ids and numbers are
//     masked so "Invalid stop ID: <uuid>" doesn't become one issue per stop
//
// Returns null for anything that isn't a 4xx axios error.
export const clientErrorReason = (e: unknown): string | null => {
  if (!isAxiosError(e)) return null;
  const status = e.response?.status;
  if (!status || status < 400 || status >= 500) return null;
  const data = e.response?.data as
    | {
        code?: unknown;
        error?: unknown;
        message?: unknown;
        validationErrors?: unknown;
      }
    | undefined;
  if (typeof data?.code === "string" && data.code) return data.code;
  const subCodes = Array.isArray(data?.validationErrors)
    ? Array.from(
        new Set(
          data.validationErrors
            .map((v) => (v as { code?: unknown } | null)?.code)
            .filter((c): c is string => typeof c === "string" && !!c)
        )
      ).sort()
    : [];
  const text =
    (typeof data?.error === "string" && data.error) ||
    (typeof data?.message === "string" &&
      data.message.trim().replace(UUID_RE, "<id>").replace(/\d+/g, "<n>")) ||
    null;
  if (text) {
    return subCodes.length ? `${text}: ${subCodes.join(",")}` : text;
  }
  return subCodes.length ? subCodes.join(",") : "no-body";
};

// A 4xx gets grouped by its server-side reason on top of the default stack
// trace grouping, and the reason goes into the title so issues for different
// causes can be told apart in the issue list.
const captureAsyncActionException = (
  e: unknown,
  extra: Record<string, unknown>
) => {
  const reason = clientErrorReason(e);
  if (reason === null) {
    Sentry.captureException(e, { extra });
    return;
  }
  const status = String((e as { response: { status: number } }).response.status);
  Sentry.withScope((scope) => {
    scope.setFingerprint(["{{ default }}", status, reason]);
    scope.setTags({ "http.status_code": status, "api.error_reason": reason });
    scope.setExtras(extra);
    scope.addEventProcessor((event) => {
      const values = event.exception?.values;
      const last = values?.[values.length - 1];
      if (last?.value) last.value = `${last.value} (${reason})`;
      return event;
    });
    Sentry.captureException(e);
  });
};

// Wraps redux's createAsyncThink in a version that logs errors
export const createErrorReportingAsyncThunk = ((
  name: any,
  action: any,
  options?: any
) =>
  createAsyncThunk(
    name,
    async (...args) => {
      try {
        return await action(...args);
      } catch (e) {
        const { dispatch } = args[1];

        const err = formatError(e);
        if (isFinanceIntegrationTokenInvalidError(e)) {
          reportWarning({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          dispatch(
            notificationActions.notifyL({
              type: "error",
              namespace: "notifications",
              key: "financeIntegrationTokenInvalid",
            })
          );
        } else if (isFinanceInvoiceRejectedError(e)) {
          reportWarning({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          dispatch(
            notificationActions.notifyL({
              type: "error",
              namespace: "notifications",
              key: "financeInvoiceRejected",
            })
          );
        } else if (isFinanceIntegrationUnreachableError(e)) {
          reportWarning({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          dispatch(
            notificationActions.notifyL({
              type: "error",
              namespace: "notifications",
              key: "financeIntegrationUnreachable",
            })
          );
        } else if (isDatabaseUnavailableResponse(e)) {
          // The database was briefly out of reach (e.g. Heroku Postgres
          // maintenance, DORA-485) and stayed so through any retries. Nothing
          // was saved and it is not a bug, so tell the user to try again
          // rather than report it.
          reportWarning({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          if (!options?.skipNotification) {
            dispatch(
              notificationActions.notifyL({
                type: "error",
                namespace: "notifications",
                key: "databaseUnavailable",
              })
            );
          }
        } else if (
          isNetworkError(e) ||
          isTimeoutError(e) ||
          isRateLimitedError(e)
        ) {
          reportWarning({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          dispatch(notificationActions.showNetworkErrorMessage());
        } else if (isUnauthenticatedError(e)) {
          // A 401 here just means the user isn't (or is no longer) logged in.
          // That's expected, not a bug — and the server's own access logs
          // already capture every 401, so there's nothing worth forwarding
          // from the client (no Sentry, no bulk log-entry, no error toast).
        } else {
          captureAsyncActionException(e, {
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          reportError({
            type: "Async action failed",
            args: args.length && args[0],
            actionType: name,
            err,
          });
          if (!options?.skipNotification) {
            dispatch(defaultErrorAction());
          }
        }
        throw e;
      }
    },
    options
  )) as any as CreateErrorReportingAsyncThunk;

type GetAsyncThunkReturn<T> =
  T extends AsyncThunk<infer U, any, any> ? U : never;
type GetAsyncThunkArg<T> = T extends AsyncThunk<any, infer U, any> ? U : never;

export const unwrap =
  <T extends AsyncThunk<any, any, any>>(asyncThunk: T) =>
  (arg: GetAsyncThunkArg<T>): AppThunkAction<Promise<GetAsyncThunkReturn<T>>> =>
  (dispatch: AppDispatch) =>
    dispatch(asyncThunk(arg)).unwrap();
